import React from 'react'

import {
  BarChart,
  Bar,
  Rectangle,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend as RechartsLegend,
  ResponsiveContainer,
} from 'recharts'

import { Stack, Typography } from '@pnld/components-web'

import { COLORS, Legend } from '../common'
import { BarChartProps, CustomTooltipProps } from './types'

const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
  if (active && payload && payload.length) {
    return (
      <Stack
        alignItems="flex-start"
        bg="white"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
        boxShadow="sm"
        maxWidth="300px"
        padding={3}
        spacing={2}
      >
        <Typography
          variant="s-xs"
          color="gray.700"
          sx={{ lineHeight: 1, fontWeight: 'bold' }}
        >
          {label}
        </Typography>
        {payload.map((entry, index) => (
          <Stack key={index} direction="row" alignItems="center" spacing={2}>
            <div
              style={{
                width: '12px',
                height: '12px',
                backgroundColor: entry.color || '#000',
                borderRadius: '2px',
              }}
            />
            <Typography
              variant="b-sm"
              color="gray.600"
              sx={{ lineHeight: 1, fontWeight: 500 }}
            >
              {entry.dataKey}:
            </Typography>
            <Typography
              variant="b-sm"
              color="blue.600"
              sx={{ lineHeight: 1, fontWeight: 600 }}
            >
              {new Intl.NumberFormat('pt-BR').format(entry.value)}
            </Typography>
          </Stack>
        ))}
      </Stack>
    )
  }

  return null
}

const CustomBarChart = ({ data, colors }: BarChartProps) => {
  const barKeys = React.useMemo(() => {
    if (data.length === 0) {
      return []
    }
    const keyNames = Object.keys(data[0]).filter(key => key !== 'name')
    return keyNames
  }, [data])

  const getColors = () => {
    if (colors && colors.length > 0) {
      return colors
    }
    return COLORS
  }

  const formatYAxisTick = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      notation: 'compact',
      compactDisplay: 'short',
    }).format(value)
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        width={500}
        height={300}
        data={data}
        margin={{
          top: 5,
          right: 30,
          left: 20,
          bottom: 5,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" />
        <YAxis tickFormatter={formatYAxisTick} />
        <Tooltip content={<CustomTooltip />} />
        {barKeys.length > 1 && <RechartsLegend content={<Legend />} />}
        {barKeys.map((key, index) => (
          <Bar
            key={key}
            dataKey={key}
            fill={getColors()[index % COLORS.length]}
            activeBar={
              <Rectangle fill="var(--chakra-colors-brand-neutral-dark_1)" />
            }
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  )
}

export default CustomBarChart
